/**
 * A measurement somebody is allowed to type.
 *
 * The layout used to offer fixed choices only, on the argument that "22mm" is
 * not a decision anybody wants to make. That holds for most people and most
 * reports, and the presets are still the fast path. It stops holding the
 * moment a report has a house style: a design manual that specifies 18mm
 * margins and 11pt body is not asking an opinion, and a builder that can only
 * approximate it produces a document that is visibly not the one that was
 * approved.
 *
 * So a measurement can be typed, and this is what keeps that safe. Every value
 * is held to a rule with a unit, a floor, a ceiling and a step, because the
 * failures worth preventing are not typos, they are plausible numbers:
 *
 *   0.3mm margins   text printing into the unprintable strip of every printer
 *   400mm margins   a page whose body is a negative height
 *   2pt body        a document nobody can read and nobody meant
 *   10.4837pt       a number that came from a calculator, not a decision
 *
 * A value that cannot be read at all falls back rather than throwing, like
 * everything else read off a stored document.
 *
 * Pure. Numbers and strings, no rendering.
 */

/** The units a page is measured in. Millimetres for paper, points for type. */
export type Unit = 'mm' | 'pt'

/** What a given measurement is allowed to be. */
export interface MeasureRule {
  readonly unit: Unit
  readonly min: number
  readonly max: number
  /**
   * The smallest increment that means anything.
   *
   * Not a slider's granularity: a rounding applied to whatever is typed, so a
   * measurement pasted from a spreadsheet arrives as a measurement rather than
   * as fourteen decimal places.
   */
  readonly step: number
}

/**
 * The number in a measurement, whatever it was written as.
 *
 * Deliberately generous about how it arrives, because it arrives from people:
 * `18`, `18mm`, `18 mm` and `18,5` are all somebody saying the same thing. The
 * unit is ignored rather than honoured, since the rule decides the unit and a
 * value typed into a field labelled mm is millimetres whatever else was typed
 * after it.
 */
export function parseMeasure(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string') return null

  // A comma decimal, as most of Europe writes one.
  const cleaned = value.trim().replace(',', '.')
  const match = /^(-?\d*\.?\d+)\s*(mm|pt|px|cm|in)?$/i.exec(cleaned)
  if (!match?.[1]) return null

  const amount = Number(match[1])
  return Number.isFinite(amount) ? amount : null
}

/**
 * A number as a measurement.
 *
 * Trailing zeros dropped, so a step of 0.5 gives `18mm` rather than `18.0mm`
 * and `10.5pt` rather than `10.50pt`. The stored value is compared against the
 * presets to decide which one is showing as chosen, so two spellings of the
 * same measurement would make a preset stop recognising itself.
 */
export function formatMeasure(amount: number, unit: Unit): string {
  // Six places is past any step this offers, and kills the floating-point
  // residue that multiplying by a step leaves behind.
  const fixed = Number(amount.toFixed(6))
  return `${fixed}${unit}`
}

/** A number held to a rule: inside the bounds, and on the step. */
export function clampMeasure(amount: number, rule: MeasureRule): number {
  const bounded = Math.min(rule.max, Math.max(rule.min, amount))
  return Math.round(bounded / rule.step) * rule.step
}

/**
 * A stored measurement, held to its rule.
 *
 * The fallback carries the load for old documents. A report written when this
 * setting was a named choice has `normal` stored where a measurement now goes;
 * that does not parse, so the caller's fallback stands, and the caller passes
 * the measurement the named choice used to mean. The document opens looking
 * exactly as it did.
 */
export function readMeasure(value: unknown, rule: MeasureRule, fallback: string): string {
  const amount = parseMeasure(value)
  if (amount === null) return fallback
  return formatMeasure(clampMeasure(amount, rule), rule.unit)
}

/**
 * Which preset a set of measurements is, if it is one of them.
 *
 * What decides whether a picker shows a preset as chosen or shows "custom".
 * Compared as strings, which is why `formatMeasure` has to be the only way a
 * measurement is written: `18mm` and `18.0mm` are the same page and would not
 * match each other.
 */
export function presetOf<T extends string>(
  presets: Readonly<Record<T, readonly string[]>>,
  measures: readonly string[],
): T | null {
  for (const [id, values] of Object.entries(presets) as [T, readonly string[]][]) {
    if (values.length !== measures.length) continue
    if (values.every((value, at) => value === measures[at])) return id
  }
  return null
}
